import { type FC, lazy } from 'react';

import { Modules, type ModulesContainer, SourceList } from './types';

import type { ModuleSkeletonProps } from '../../vars';

const AktionssliderSkeleton = lazy(() => import('../aktionsslider/skeleton' /* webpackChunkName: "AktionssliderSkeleton" */));
const ConfigListingSkeleton = lazy(() => import('../configlisting/skeleton' /* webpackChunkName: "ConfigListingSkeleton" */));
const ErweiterteSchnellsucheSkeleton = lazy(() => import('../erweiterte-schnellsuche/skeleton' /* webpackChunkName: "ErweiterteSchnellsucheSkeleton" */));
const FahrzeuglisteSkeleton = lazy(() => import('../fahrzeugliste/skeleton' /* webpackChunkName: "FahrzeuglisteSkeleton" */));
const FahrzeugsliderSkeleton = lazy(() => import('../fahrzeugslider/skeleton' /* webpackChunkName: "FahrzeugsliderSkeleton" */));
const FahrzeugDetailsSkeleton = lazy(() => import('../fahrzeugsuche/details/skeleton' /* webpackChunkName: "FahrzeugDetailsSkeleton" */));
const FahrzeugsucheListSkeleton = lazy(() => import('../fahrzeugsuche/list/skeleton' /* webpackChunkName: "FahrzeugsucheListSkeleton" */));
const FinanzierungsansichtSkeleton = lazy(() => import('../finanzierungsansicht/skeleton' /* webpackChunkName: "FinanzierungsansichtSkeleton" */));
const InstallSkeleton = lazy(() => import('../installdoc/skeleton' /* webpackChunkName: "InstallDocSkeleton" */));
const MenuSkeleton = lazy(() => import('../menu/skeleton' /* webpackChunkName: "MenuSkeleton" */));
const PrintSkeleton = lazy(() => import('../print/skeleton' /* webpackChunkName: "PrintSkeleton" */));
const SchnellsucheSkeleton = lazy(() => import('../schnellsuche/skeleton' /* webpackChunkName: "SchnellsucheSkeleton" */));
const StandorteSkeleton = lazy(() => import('../standorte/skeleton' /* webpackChunkName: "StandorteSkeleton" */));

const Aktionsslider = lazy(() => import('../aktionsslider' /* webpackChunkName: "Aktionsslider" */));
const ConfigListing = lazy(() => import('../configlisting' /* webpackChunkName: "ConfigListing" */));
const ErweiterteSchnellsuche = lazy(() => import('../erweiterte-schnellsuche' /* webpackChunkName: "ErweiterteSchnellsuche" */));
const Fahrzeugliste = lazy(() => import('../fahrzeugliste' /* webpackChunkName: "Fahrzeugliste" */));
const Fahrzeugslider = lazy(() => import('../fahrzeugslider' /* webpackChunkName: "Fahrzeugslider" */));
const Fahrzeugsuche = lazy(() => import('../fahrzeugsuche' /* webpackChunkName: "Fahrzeugsuche" */));
const Finanzierungsansicht = lazy(() => import('../finanzierungsansicht' /* webpackChunkName: "Finanzierungsansicht" */));
const InstallDoc = lazy(() => import('../installdoc' /* webpackChunkName: "InstallDoc" */));
const Menu = lazy(() => import('../menu' /* webpackChunkName: "Menu" */));
const Print = lazy(() => import('../print' /* webpackChunkName: "Print" */));
const Schnellsuche = lazy(() => import('../schnellsuche' /* webpackChunkName: "Schnellsuche" */));
const Standorte = lazy(() => import('../standorte' /* webpackChunkName: "Standorte" */));

const modules: {
    Component: FC;
    i: Modules;
    t: ModulesContainer;
    noInstall: boolean;
    path?: string;
    title?: string;
    Skeleton: FC<ModuleSkeletonProps>;
    SkeletonDetails?: FC<ModuleSkeletonProps>;
    vehicleList?: SourceList;
}[] = [
    {
        i: Modules.Aktionsslider,
        t: 'ahdAktion',
        path: '/aktionsslider',
        title: 'Aktionsslider',
        vehicleList: SourceList?.Aktionsslider,
        noInstall: false,
        Component: Aktionsslider,
        Skeleton: AktionssliderSkeleton,
    },
    {
        i: Modules.ErweiterteSchnellsuche,
        t: 'ahdErweiterteSchnellsuche',
        path: '/erweiterteSchnellsuche',
        title: 'Erweiterte Schnellsuche',
        vehicleList: SourceList?.ErweiterteSchnellsuche,
        noInstall: false,
        Component: ErweiterteSchnellsuche,
        Skeleton: ErweiterteSchnellsucheSkeleton,
    },
    {
        i: Modules.Fahrzeugliste,
        t: 'ahdFahrzeugliste',
        path: '/fahrzeugliste',
        title: 'Fahrzeugliste',
        vehicleList: SourceList?.Fahrzeugliste,
        noInstall: false,
        Component: Fahrzeugliste,
        Skeleton: FahrzeuglisteSkeleton,
    },
    {
        i: Modules.Fahrzeugslider,
        t: 'ahdFahrzeugslider',
        path: '/fahrzeugslider',
        title: 'Fahrzeugslider',
        vehicleList: SourceList?.Fahrzeugslider,
        noInstall: false,
        Component: Fahrzeugslider,
        Skeleton: FahrzeugsliderSkeleton,
    },
    {
        i: Modules.Fahrzeugsuche,
        t: 'ahdFahrzeugsuche',
        path: '/fahrzeugsuche',
        title: 'Fahrzeugsuche',
        vehicleList: SourceList?.Fahrzeugsuche,
        noInstall: false,
        Component: Fahrzeugsuche,
        Skeleton: FahrzeugsucheListSkeleton,
        SkeletonDetails: FahrzeugDetailsSkeleton,
    },
    {
        i: Modules.Finanzierungsansicht,
        t: 'ahdFinanzierung',
        path: '/finanzierungsansicht',
        title: 'Finanzierung',
        vehicleList: SourceList?.Finanzierungsansicht,
        noInstall: false,
        Component: Finanzierungsansicht,
        Skeleton: FinanzierungsansichtSkeleton,
    },
    {
        i: Modules.Schnellsuche,
        t: 'ahdSchnellsuche',
        path: '/schnellsuche',
        title: 'Schnellsuche',
        vehicleList: SourceList?.Schnellsuche,
        noInstall: false,
        Component: Schnellsuche,
        Skeleton: SchnellsucheSkeleton,
    },
    { i: Modules.Standorte, t: 'ahdStandorte', path: '/standorte', title: 'Standorte', vehicleList: SourceList?.Standorte, noInstall: false, Component: Standorte, Skeleton: StandorteSkeleton },

    // BACKGROUND STUFF
    { i: Modules.ConfigListing, t: 'ahdConfigListing', noInstall: true, Component: ConfigListing, Skeleton: ConfigListingSkeleton },
    { i: Modules.Install, t: 'ahdInstallDocumentationSite', noInstall: true, Component: InstallDoc, Skeleton: InstallSkeleton },
    { i: Modules.Menu, t: 'ahdMenu', noInstall: true, Component: Menu, Skeleton: MenuSkeleton },
    { i: Modules.Print, t: 'ahdPrint', noInstall: true, Component: Print, Skeleton: PrintSkeleton },
];

export default modules;
